/**
 * Shared icon strip rendered on every order row (list page) and at the top
 * of every order detail page. Actions, each gated on relevance and the
 * caller's capability:
 *
 *   Magnifying glass  -- open MatchCustomerModal (when the order needs a
 *                        Sage customer link)
 *   Truck             -- ship (only when ship_status is eligible or failed)
 *   ExternalLink      -- open source-doc PDF in a new tab (COCKPIT.md 2.10)
 *   MessageSquare     -- notes popover with has-notes indicator (COCKPIT.md 2.10)
 *
 * Tooltips use native title= for portability across browsers and screen
 * readers; no custom popover library required.
 */

import { Loader2, Search, Truck } from 'lucide-react';
import type { OrderRow } from '@/lib/useOrders';
import { API_BASE } from '@/lib/apiBase';
import { OpenDocumentButton } from '@/components/RowActionIcons';
import { OrderNotesPopover } from './OrderNotesPopover';

type ActionSize = 'sm' | 'md';

type Props = {
  row: Pick<
    OrderRow,
    | 'order_id'
    | 'source_channel'
    | 'source_order_id'
    | 'partner_id'
    | 'status'
    | 'ship_status'
    | 'notes_count'
  >;
  size?: ActionSize;
  isAdmin: boolean;
  isEditor: boolean;
  shippingOrderId: string | null;
  onMatch: (orderId: string) => void;
  onShip: (orderId: string) => void;
};

export function OrderActions({
  row,
  size = 'sm',
  isAdmin,
  isEditor,
  shippingOrderId,
  onMatch,
  onShip,
}: Props) {
  // Show the match action only when the row genuinely needs a Sage customer
  // link AND it hasn't already advanced past the point where Sage owns the
  // customer record. Once a row reaches in_sage or later, Sage has matched
  // the customer regardless of what Cockpit's partner_id field says, so the
  // icon should stay disabled. The reconcile Lambda backfills partner_id from
  // sage_so_sales_order_header_mirror.CustomerNo on the same pass that advances the row;
  // this status gate is defense-in-depth for rows where the backfill hasn't
  // landed yet, so the icon never lies about needing a customer match.
  const statusOwnsCustomerInSage =
    row.status === 'in_sage' ||
    row.status === 'invoiced' ||
    row.status === 'shipped' ||
    row.status === 'cancelled';
  const needsMatch = !row.partner_id && !statusOwnsCustomerInSage;
  const matchTooltip = needsMatch
    ? 'Match a Sage customer'
    : !row.partner_id && statusOwnsCustomerInSage
      ? 'Customer matched in Sage'
      : 'Customer already matched';
  const isShipable =
    row.ship_status === 'failed' ||
    (row.ship_status === 'eligible' && row.status !== 'shipped');
  const isShipping = shippingOrderId === row.order_id;
  // The /source-doc proxy authenticates against Faire with the app
  // credentials and streams the packing-slip PDF back. New tab so the
  // browser's PDF viewer handles it.
  const sourceDocUrl =
    row.source_channel === 'faire'
      ? `${API_BASE}/v1/commerce/orders/${row.order_id}/source-doc`
      : null;

  const iconSize = size === 'sm' ? 14 : 16;
  const btnCls =
    size === 'sm'
      ? 'inline-flex items-center justify-center rounded border border-amp-line w-7 h-7 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
      : 'inline-flex items-center justify-center rounded border border-amp-line w-8 h-8 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';

  return (
    <div className="inline-flex items-center gap-1.5">
      {isEditor && (
        <button
          type="button"
          onClick={() => onMatch(row.order_id)}
          disabled={!needsMatch}
          title={matchTooltip}
          aria-label="Match customer"
          className={btnCls}
        >
          <Search size={iconSize} aria-hidden />
        </button>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={() => onShip(row.order_id)}
          disabled={!isShipable || isShipping}
          title={
            row.ship_status === 'failed'
              ? 'Retry ship'
              : row.ship_status === 'eligible' && row.status !== 'shipped'
                ? 'Ship now'
                : row.ship_status === 'eligible'
                  ? 'Already shipped'
                  : 'Not ready to ship'
          }
          aria-label="Ship order"
          className={btnCls}
        >
          {isShipping ? (
            <Loader2 size={iconSize} className="animate-spin" aria-hidden />
          ) : (
            <Truck size={iconSize} aria-hidden />
          )}
        </button>
      )}
      <OpenDocumentButton
        url={sourceDocUrl ?? ''}
        label="View packing slip PDF"
        disabled={!sourceDocUrl}
      />
      <OrderNotesPopover
        orderId={row.order_id}
        sourceOrderId={row.source_order_id}
        notesCount={row.notes_count ?? 0}
        canWrite={isEditor}
      />
    </div>
  );
}
